// Data access for the unified conversations model (phase 2a schema).
//
// The generated supabase types (src/integrations/supabase/types.ts) are
// produced by Lovable Cloud and don't know the new tables yet, so this module
// is the single place that talks to them through a narrow, lint-clean
// structural cast. When the types regenerate, callers don't change.

import { supabase } from "@/integrations/supabase/client";

export type ConversationKind = "renter_owner" | "user_admin";
export type SenderRole = "renter" | "owner" | "user" | "admin" | "system" | "assistant";

export interface ConversationRow {
  id: string;
  tenant_id: string;
  kind: ConversationKind;
  property_id: string | null;
  contact_request_id: string | null;
  user_id: string | null;
  subject: string | null;
  status: "open" | "closed";
  created_at: string;
  last_message_at: string;
}

export interface MessageRow {
  id: string;
  conversation_id: string;
  sender_user_id: string | null;
  sender_role: SenderRole;
  body: string;
  created_at: string;
}

export interface ContactRequestLite {
  id: string;
  property_id: string;
  renter_id: string;
  owner_id: string | null;
  status: "pending" | "accepted" | "declined" | "archived";
  direction: "renter_to_owner" | "owner_to_renter";
  move_in_date?: string | null;
  move_out_date?: string | null;
  /** Set when the system closed the request (listing paused / dates changed),
   *  or "declined:<reason>" when the owner said no with a reason. */
  closed_reason?: string | null;
  /** The owner's note when saying no. */
  decline_note?: string | null;
  auto_closed_at?: string | null;
}

interface QueryResult<T> {
  data: T | null;
  error: { message: string } | null;
}

// Minimal structural view of the client for tables outside the generated types.
interface UntypedDb {
  from(table: string): {
    select(cols: string): {
      order(col: string, opts: { ascending: boolean }): PromiseLike<QueryResult<unknown[]>>;
      eq(col: string, v: string): {
        order(col: string, opts: { ascending: boolean }): PromiseLike<QueryResult<unknown[]>>;
      };
      in(col: string, vals: string[]): PromiseLike<QueryResult<unknown[]>>;
    };
  };
  rpc(fn: string, args?: Record<string, unknown>): PromiseLike<QueryResult<unknown>>;
}

const db = supabase as unknown as UntypedDb;

export async function listConversations(): Promise<ConversationRow[]> {
  const { data, error } = await db
    .from("conversations")
    .select("*")
    .order("last_message_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as ConversationRow[];
}

export async function listMessages(conversationId: string): Promise<MessageRow[]> {
  const { data, error } = await db
    .from("messages")
    .select("*")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as MessageRow[];
}

export async function fetchContactRequests(ids: string[]): Promise<ContactRequestLite[]> {
  if (ids.length === 0) return [];
  const { data, error } = await db
    .from("contact_requests")
    .select("id, property_id, renter_id, owner_id, status, direction, move_in_date, move_out_date, closed_reason, auto_closed_at, decline_note")
    .in("id", ids);
  if (error) throw new Error(error.message);
  return (data ?? []) as ContactRequestLite[];
}

export interface ParticipantRow {
  conversation_id: string;
  user_id: string;
  role: string;
  last_read_at: string | null;
}

/** The signed-in user's own membership rows (RLS returns only self). */
export async function fetchMyParticipantRows(): Promise<ParticipantRow[]> {
  const { data, error } = await db
    .from("conversation_participants")
    .select("*")
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ParticipantRow[];
}

export async function sendMessage(conversationId: string, body: string): Promise<void> {
  const { error } = await db.rpc("send_message", {
    p_conversation_id: conversationId,
    p_body: body,
  });
  if (error) throw new Error(error.message);
}

/** Renter re-sends a request the system closed; the DB checks the new dates fit. */
export async function reopenContactRequest(
  requestId: string,
  moveIn: string | null,
  moveOut: string | null,
  message: string | null,
): Promise<void> {
  const { error } = await db.rpc("reopen_contact_request", {
    p_request_id: requestId,
    p_move_in: moveIn,
    p_move_out: moveOut,
    p_message: message,
  });
  if (error) throw new Error(error.message);
}

export async function markConversationRead(conversationId: string): Promise<void> {
  const { error } = await db.rpc("mark_conversation_read", {
    p_conversation_id: conversationId,
  });
  if (error) throw new Error(error.message);
}

export async function countUnreadConversations(): Promise<number> {
  const { data, error } = await db.rpc("count_unread_conversations");
  if (error) throw new Error(error.message);
  return typeof data === "number" ? data : 0;
}

export async function startAdminConversation(subject: string | null, body: string, tenantId: string): Promise<string> {
  const { data, error } = await db.rpc("start_admin_conversation", {
    p_subject: subject,
    p_body: body,
    p_tenant_id: tenantId,
  });
  if (error) throw new Error(error.message);
  return String(data);
}
